import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { company, offices } from "@/data/company";

const LAST_UPDATED = "30 April 2026";
const EFFECTIVE_FROM = "1 May 2026";

const SECTION_KEYS = [
  "intro",
  "data",
  "use",
  "legal",
  "share",
  "retain",
  "security",
  "transfers",
  "rights",
  "cookies",
  "changes",
  "contact",
];

export default function Privacy() {
  const { t } = useTranslation();
  const notice = t("privacy.translation_notice");

  return (
    <>
      {/* Hero */}
      <section className="bg-brand-dark text-white pt-xl pb-lg relative overflow-hidden" data-testid="privacy-hero">
        <div className="absolute inset-0 tech-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-brand/15 blur-3xl pointer-events-none" />
        <div className="max-w-[900px] mx-auto px-6 relative z-10 pt-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand text-[10px] font-bold uppercase tracking-widest">
              <span className="material-symbols-outlined text-[14px]">shield</span>
              {t("privacy.eyebrow")}
            </span>
            <h1 className="font-h1 text-5xl md:text-6xl font-extrabold mt-5 tracking-tight leading-[1.05]">
              {t("privacy.title")}{" "}
              <span className="bg-gradient-to-r from-brand via-emerald-300 to-teal-300 bg-clip-text text-transparent">
                {t("privacy.title_accent")}
              </span>
            </h1>
            <p className="text-zinc-300 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
              {t("privacy.subtitle")}
            </p>
            <div className="flex flex-wrap items-center gap-6 mt-6 text-xs font-mono text-zinc-400">
              <span>
                <span className="text-brand uppercase tracking-widest">{t("privacy.last_updated_label")}</span>
                <span className="mx-2">·</span>
                <span className="text-white font-bold">{LAST_UPDATED}</span>
              </span>
              <span>
                <span className="text-brand uppercase tracking-widest">{t("privacy.effective_label")}</span>
                <span className="mx-2">·</span>
                <span className="text-white font-bold">{EFFECTIVE_FROM}</span>
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-xl bg-surface">
        <div className="max-w-[900px] mx-auto px-6 grid lg:grid-cols-[240px_1fr] gap-gutter">
          {/* Sticky TOC */}
          <aside className="lg:sticky lg:top-24 h-fit hidden lg:block" data-testid="privacy-toc">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold font-mono mb-3">
              {t("privacy.contents")}
            </div>
            <ol className="space-y-2">
              {SECTION_KEYS.map((id) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="text-sm text-on-surface-variant hover:text-brand transition-colors block leading-snug"
                  >
                    {t(`privacy.section_titles.${id}`)}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          {/* Article */}
          <article className="prose prose-zinc max-w-none prose-headings:font-manrope prose-headings:font-bold prose-headings:text-primary prose-p:text-on-surface-variant prose-p:leading-relaxed prose-strong:text-primary">
            {/* Convenience-translation notice for non-English locales */}
            {notice && (
              <div
                data-testid="privacy-translation-notice"
                className="not-prose mb-lg flex items-start gap-3 p-4 rounded-xl border border-brand/30 bg-brand/5"
              >
                <span className="material-symbols-outlined text-brand text-[20px] mt-0.5">info</span>
                <p className="text-sm text-on-surface leading-relaxed m-0">{notice}</p>
              </div>
            )}

            {SECTION_KEYS.map((id, i) => (
              <motion.section
                key={id}
                id={id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="scroll-mt-28 mb-lg"
                data-testid={`privacy-section-${id}`}
              >
                <h2 className="font-h3 text-h3 text-primary mb-3">
                  {t(`privacy.section_titles.${id}`)}
                </h2>
                <div
                  className="space-y-3 text-base text-on-surface-variant leading-relaxed"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: t(`privacy.sections.${id}`) }}
                />
                {id === "contact" && (
                  <div className="bg-surface-container-low border border-outline-variant rounded-xl p-5 mt-3">
                    <div className="font-manrope font-bold text-primary">{company.parent}</div>
                    <div className="text-sm text-on-surface-variant mt-1">
                      Attn: Data Protection
                    </div>
                    <div className="text-sm mt-2">
                      <span className="material-symbols-outlined text-brand text-[15px] align-middle mr-1">
                        mail
                      </span>
                      <a
                        href={`mailto:${company.email.contact}`}
                        className="text-brand hover:underline"
                      >
                        {company.email.contact}
                      </a>
                    </div>
                    <div className="text-sm mt-1">
                      <span className="material-symbols-outlined text-brand text-[15px] align-middle mr-1">
                        location_on
                      </span>
                      806 East Palm Lane, Phoenix, AZ 85006, USA (Head Office)
                    </div>
                  </div>
                )}
              </motion.section>
            ))}

            <div className="not-prose mt-xl pt-lg border-t border-outline-variant">
              <p className="text-xs text-on-surface-variant">
                {t("contact.offices.eyebrow")}: {offices.map((o) => o.country).join(" · ")}
              </p>
              <p className="text-xs text-on-surface-variant mt-1">
                © {new Date().getFullYear()} {company.parent}™ · {t("footer.rights")}
              </p>
              <div className="mt-4">
                <Link
                  to="/contact"
                  data-testid="privacy-contact-link"
                  className="inline-flex items-center gap-2 text-sm text-brand font-bold hover:underline"
                >
                  {t("privacy.contact_cta")}
                  <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
